import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X } from 'lucide-react';

const STORAGE_KEY = 'cookie_notice_ack_v1';

/**
 * Informative cookie banner (LSSI Art. 22.2 exception).
 * The platform uses ONLY strictly necessary cookies (Supabase auth session,
 * language preference, this acknowledgement flag). No analytics, no tracking,
 * no third-party marketing cookies — therefore prior opt-in consent is not
 * required, only transparent information per AEPD 2023 guide.
 */
const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.localStorage.getItem(STORAGE_KEY)) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      // ignore (private mode)
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 pointer-events-none">
      <Card className="max-w-3xl mx-auto p-4 bg-background/95 backdrop-blur border shadow-lg pointer-events-auto relative">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pr-8">
          <div className="flex-1 text-sm">
            <p className="text-foreground">
              We only use <strong>strictly necessary cookies</strong> to keep your session
              active and remember your language preference. We don&apos;t use analytics or
              advertising trackers.{' '}
              <Link to="/cookies" className="text-primary hover:underline font-medium">
                More info
              </Link>
              .
            </p>
          </div>
          <Button onClick={dismiss} size="sm" className="whitespace-nowrap shrink-0">
            Got it
          </Button>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 p-1 rounded hover:bg-muted text-muted-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </Card>
    </div>
  );
};

export default CookieBanner;
